import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Icon,
  IconButton,
  MenuItem,
  TextField
} from '@material-ui/core';
import NumberFormat from 'react-number-format';
import { Formik, FieldArray } from 'formik';
import * as yup from 'yup';
import { addTasksToQueueApi } from 'api';
import {
  frequencyOptions,
  emptySubscription
} from '../Subscriptions/helpers';
import { useStyles } from '../Subscriptions/styles';


const AddMembershipModal = ({
  openAddingMembershipsDialog,
  setOpenAddingMembershipsDialog,
  handleSaveMembershipOption
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { editableQuoteProduct, quoteProductSubscriptionOptions } = useSelector((state: IReduxState) => state.quote);
  const { profile: { stores } } = useSelector((state: IReduxState) => state.user);
  const [{ quoteProStoreCode }] = stores;
  const sortOrdersArray = quoteProductSubscriptionOptions.map((option) => option.ssort_order);

  const handleSubmitMembershipOptions = (values) => {
    const membershipOptions = values.memberships.map(({ percent_discount, first_period_price, frequency, ...optionData }) => optionData);

    handleSaveMembershipOption(membershipOptions);

    const subscriptionActiveAttr = editableQuoteProduct?.custom_attributes.find((attr) => attr.attribute_code === 'subscription_active');

    if (subscriptionActiveAttr?.value === '0') {
      const productToUpdate = {
        sku: editableQuoteProduct?.sku,
        custom_attributes: [
          { attribute_code: 'subscription_active', value: 1 },
        ],
      };

      const task = {
        body: productToUpdate,
        name: editableQuoteProduct?.name,
        sku: editableQuoteProduct?.sku,
        requestType: 'UPDATE_PRODUCT',
        storeViewCode: quoteProStoreCode,
      };
      addTasksToQueueApi({ dispatch, tasks: [task] });
    }
  };

  const handleFrequencyChange = (event, index, setFieldValue, handleChange) => {
    const { value } = event.target;
    const [frequencyCount, frequencyUnit] = value.split(/\s+/);

    setFieldValue(`memberships[${index}].frequency_unit`, frequencyUnit.replace('s', ''));
    setFieldValue(`memberships[${index}].frequency_count`, frequencyCount);
    handleChange(event);
  };

  const handlePriceChange = (fieldName, index, floatValue, setFieldValue, values) => {
    const { installment_price, first_period_price, adjustment_price } = values.memberships[index];
    let newInstallmentPrice = installment_price;
    let newFirstPeriodPrice = first_period_price;
    let newAdjustmentPrice = adjustment_price;

    if (fieldName === 'first_period_price') {
      newFirstPeriodPrice = floatValue;
      newAdjustmentPrice = newFirstPeriodPrice - installment_price;
    } else if (fieldName === 'installment_price') {
      newInstallmentPrice = floatValue;
      newAdjustmentPrice = first_period_price - newInstallmentPrice;
    }

    setFieldValue(`memberships[${index}].first_period_price`, newFirstPeriodPrice);
    setFieldValue(`memberships[${index}].installment_price`, newInstallmentPrice);
    setFieldValue(`memberships[${index}].adjustment_price`, newAdjustmentPrice);
  };

  return (
    <Dialog
      className={classes.dialog}
      open={openAddingMembershipsDialog}
      onClose={() => setOpenAddingMembershipsDialog(false)}
      aria-labelledby="add-subscription-option"
    >
      <DialogTitle id="add-subscription-option">Membership Subscription Options:</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Choose options for membership subscription
        </DialogContentText>
        <Formik
          initialValues={{
            memberships: [
              {
                product_id: editableQuoteProduct?.id,
                frequency_count: '',
                frequency_unit: '',
                sproduct_label: '',
                first_period_price: 0,
                installment_price: editableQuoteProduct?.price,
                adjustment_price: 0,
                additional_information: '',
                ssort_order: '',
                frequency: '',
                percent_discount: ''
              }
            ]
          }}
          enableReinitialize
          validationSchema={
            yup.object({
              memberships: yup
                .array().of(
                  yup.object().shape({
                    frequency: yup
                      .string()
                      .required('Field is required'),
                    first_period_price: yup
                      .number().typeError('Allowed only numerical symbols')
                      .positive('Value must be positive')
                      .required('Field is required'),
                    installment_price: yup
                      .number().typeError('Allowed only numerical symbols')
                      .positive('Value must be positive')
                      .required('Field is required'),
                    sproduct_label: yup
                      .string()
                      .required('Field is required'),
                    ssort_order: yup
                      .number().typeError('Allowed only numerical symbols')
                      .positive('Value must be positive')
                      .required('Field is required')
                      .test('ssort_order', 'Sort Order is already exists', (value) => !sortOrdersArray.includes(value))
                  })
                )
            })
          }
          onSubmit={handleSubmitMembershipOptions}
        >
          {({
            values,
            errors,
            isValid,
            dirty,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => (
            <form onSubmit={handleSubmit}>
              <FieldArray name="memberships">
                {({push, remove}) => {
                  return (
                    <>
                      <Grid item xs={12}>
                        {values.memberships.map((_, index) => (
                          <>
                            <CardContent key={`option-card-${index}`} className="px-0 flex items-center justify-around">
                              <Grid container xs={12} className="p-0">
                                <TextField
                                  className="min-w-260 mr-2"
                                  name={`memberships[${index}].frequency`}
                                  select
                                  label="Frequency"
                                  value={values.memberships[index].frequency}
                                  variant="outlined"
                                  onChange={(event) => handleFrequencyChange(event, index, setFieldValue, handleChange)}
                                  onBlur={handleBlur}
                                  inputProps={{ className: 'text-center' }}
                                  error={errors.memberships?.length && Boolean(errors.memberships[index]?.frequency)}
                                  helperText={errors.memberships?.length && errors.memberships[index]?.frequency}
                                >
                                  {frequencyOptions.map((option, idx) => (
                                    <MenuItem
                                      key={`option-${idx}`}
                                      value={option}
                                    >
                                      {option}
                                    </MenuItem>
                                  ))}
                                </TextField>

                                <NumberFormat
                                  required
                                  className="min-w-260"
                                  prefix="$ "
                                  fixedDecimalScale
                                  decimalScale={2}
                                  thousandSeparator
                                  label={'First Period Price'}
                                  variant="outlined"
                                  name="memberships.first_period_price"
                                  value={values.memberships.first_period_price}
                                  onValueChange={({ floatValue }) => handlePriceChange('first_period_price', index, floatValue, setFieldValue, values)}
                                  onBlur={handleBlur}
                                  error={errors.memberships?.length && Boolean(errors.memberships[index]?.first_period_price)}
                                  helperText={errors.memberships?.length && errors.memberships[index]?.first_period_price}
                                  customInput={TextField}
                                  inputProps={{ className: 'text-center' }}
                                />

                                <NumberFormat
                                  required
                                  className="min-w-260 mt-4 mr-2"
                                  prefix="$ "
                                  fixedDecimalScale
                                  decimalScale={2}
                                  thousandSeparator
                                  label={'Recurring Price'}
                                  variant="outlined"
                                  name="memberships.installment_price"
                                  value={values.memberships.installment_price}
                                  onValueChange={({ floatValue }) => handlePriceChange('installment_price', index, floatValue, setFieldValue, values)}
                                  onBlur={handleBlur}
                                  error={errors.memberships?.length && Boolean(errors.memberships[index]?.installment_price)}
                                  helperText={errors.memberships?.length && errors.memberships[index]?.installment_price}
                                  customInput={TextField}
                                  inputProps={{ className: 'text-center' }}
                                />

                                <TextField
                                  required
                                  className="mt-4 min-w-260"
                                  name={`memberships[${index}].sproduct_label`}
                                  label={'Product Label'}
                                  value={values.memberships[index].sproduct_label}
                                  variant="outlined"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  inputProps={{ className: 'text-center' }}
                                  error={errors.memberships?.length && Boolean((errors.memberships[index])?.sproduct_label)}
                                  helperText={errors.memberships?.length && (errors.memberships[index])?.sproduct_label}
                                />
                                <NumberFormat
                                  required
                                  allowNegative={false}
                                  className="min-w-260 mt-4 mr-2"
                                  label={'Sort Order'}
                                  variant="outlined"
                                  name={`memberships[${index}].ssort_order`}
                                  value={values.memberships[index].ssort_order}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.memberships?.length && Boolean((errors.memberships[index])?.ssort_order)}
                                  helperText={errors.memberships?.length && (errors.memberships[index])?.ssort_order}
                                  customInput={TextField}
                                  inputProps={{ className: 'text-center' }}
                                />
                              </Grid>
                              {values.memberships.length > 1 &&
                              <div>
                                <IconButton
                                  onClick={() => remove(index)}
                                  title="Delete"
                                >
                                  <Icon>delete</Icon>
                                </IconButton>
                              </div>
                              }
                            </CardContent>
                            <Grid item xs={12}>
                              {(values.memberships.length !== (index + 1)) && <Divider className="my-5" />}
                            </Grid>
                          </>
                        ))}
                        <DialogActions className="m-4">
                          <Button
                            variant="outlined"
                            color="primary"
                            disabled={!(isValid && dirty)}
                            onClick={() => push(emptySubscription(editableQuoteProduct?.id))}
                          >
                            <span className="capitalize">Add more membership subscriptions</span>
                          </Button>
                          <Button
                            className="text-muted"
                            onClick={() => setOpenAddingMembershipsDialog(false)}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            disabled={!(isValid && dirty)}
                            color="primary"
                            variant="contained"
                          >
                            Create Option
                          </Button>
                        </DialogActions>
                      </Grid>
                    </>
                  );
                }}
              </FieldArray>
            </form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default AddMembershipModal;
